import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, FormControl, InputLabel, Select, MenuItem, Alert, Typography,
} from '@mui/material';
import { Save } from '@mui/icons-material';
import { useFormik } from 'formik';
import { expenseSchema } from './expenseSchema';
import PaymentMethodFields from '../common/PaymentMethodFields';
import { format } from 'date-fns';
import { Expense, ExpenseCategory, ExpenseSubCategory } from '../../types';
import expenseCategoryService from '../../services/expenseCategoryService';
import { useAuth } from '../../context/AuthContext';
import { useTimezone } from '../../hooks/useTimezone';

interface Props {
  open: boolean;
  expense: Expense | null;
  onClose: () => void;
  onSuccess: () => void;
  onSubmit: (id: string, data: FormData) => Promise<void>;
}

const validationSchema = expenseSchema;

const EditExpenseDialog: React.FC<Props> = ({ open, expense, onClose, onSuccess, onSubmit }) => {
  const { currency } = useAuth();
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [subCategories, setSubCategories] = useState<ExpenseSubCategory[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      expenseCategoryService.getCategories().then(setCategories).catch(() => {});
      setFile(null);
    }
  }, [open]);

  useEffect(() => {
    if (open && expense?.categoryId?._id) {
      expenseCategoryService.getSubCategories(expense.categoryId._id)
        .then(setSubCategories).catch(() => setSubCategories([]));
    } else {
      setSubCategories([]);
    }
  }, [open, expense?.categoryId?._id]);

  // An expense can't be dated in the future — the picker stops at today (school time).
  const { getToday } = useTimezone();
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      title: expense?.title || '',
      amount: expense?.amount?.toString() || '',
      date: expense?.date ? format(new Date(expense.date), 'yyyy-MM-dd') : '',
      categoryId: expense?.categoryId?._id || '',
      subCategoryId: expense?.subCategoryId?._id || '',
      notes: expense?.notes || '',
      method: expense?.method || 'cash',
      referenceNumber: expense?.referenceNumber || '',
    },
    validationSchema,
    onSubmit: async (values) => {
      if (!expense) return;
      setLoading(true);
      try {
        const formData = new FormData();
        formData.append('title', values.title);
        formData.append('amount', values.amount.toString());
        formData.append('date', values.date);
        formData.append('categoryId', values.categoryId);
        formData.append('subCategoryId', values.subCategoryId);
        formData.append('notes', values.notes);
        formData.append('method', values.method);
        formData.append('referenceNumber', values.referenceNumber.trim());
        if (file) formData.append('expenseDocument', file);

        await onSubmit(expense._id, formData);
        onSuccess();
      } catch (error: any) {
        throw error;
      } finally {
        setLoading(false);
      }
    },
  });

  const handleCategoryChange = async (categoryId: string) => {
    formik.setFieldValue('categoryId', categoryId);
    formik.setFieldValue('subCategoryId', '');
    if (categoryId) {
      try {
        const subs = await expenseCategoryService.getSubCategories(categoryId);
        setSubCategories(subs);
      } catch {
        setSubCategories([]);
      }
    } else {
      setSubCategories([]);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Expense</DialogTitle>
      <form onSubmit={formik.handleSubmit}>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid size={12}>
              <TextField
                fullWidth name="title" label="Title"
                value={formik.values.title} onChange={formik.handleChange}
                error={formik.touched.title && Boolean(formik.errors.title)}
                helperText={formik.touched.title && formik.errors.title}
                margin="normal"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth name="amount" label={`Amount (${currency})`} type="number"
                value={formik.values.amount} onChange={formik.handleChange}
                error={formik.touched.amount && Boolean(formik.errors.amount)}
                helperText={formik.touched.amount && formik.errors.amount}
                margin="normal" inputProps={{ min: 0, step: '0.01' }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth name="date" label="Date" type="date"
                value={formik.values.date} onChange={formik.handleChange}
                error={formik.touched.date && Boolean(formik.errors.date)}
                helperText={formik.touched.date && formik.errors.date}
                margin="normal" inputProps={{ max: getToday() }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl
                fullWidth
                margin="normal"
                error={formik.touched.categoryId && Boolean(formik.errors.categoryId)}
              >
                <InputLabel>Category *</InputLabel>
                <Select
                  value={formik.values.categoryId}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  onBlur={() => formik.setFieldTouched('categoryId', true)}
                  label="Category *"
                >
                  {categories.map((c) => <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>)}
                </Select>
                {formik.touched.categoryId && formik.errors.categoryId && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                    {formik.errors.categoryId}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth margin="normal" disabled={!formik.values.categoryId || subCategories.length === 0}>
                <InputLabel>Sub-Category</InputLabel>
                <Select value={formik.values.subCategoryId} onChange={formik.handleChange} name="subCategoryId" label="Sub-Category">
                  <MenuItem value="">None</MenuItem>
                  {subCategories.map((s) => <MenuItem key={s._id} value={s._id}>{s.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <PaymentMethodFields formik={formik} label="Paid by" />
            <Grid size={12}>
              <TextField fullWidth name="notes" label="Notes" multiline rows={3}
                value={formik.values.notes} onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.notes && Boolean(formik.errors.notes)}
                helperText={formik.touched.notes && formik.errors.notes}
                margin="normal"
              />
            </Grid>
            <Grid size={12}>
              <TextField fullWidth type="file" label="Replace Receipt (Image/PDF)"
                InputLabelProps={{ shrink: true }} inputProps={{ accept: 'image/*,application/pdf' }}
                onChange={(e: any) => setFile(e.target.files?.[0] || null)} margin="normal"
              />
              {expense?.documentName && !file && (
                <Alert severity="info" sx={{ mt: 1 }}>Current: {expense.documentName}</Alert>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" startIcon={<Save />} disabled={loading}>
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditExpenseDialog;
